import { defineStore } from "pinia";

import { Create, Update } from "../../service/user/interface/user.interface";
import userService from "../../service/user/user.service";
import { storeItem } from "../../core/utils/storage.helper";
import { ref } from "vue";

const USER_INFO_KEY = "platoon_user_info";

function loadUserInfo(): {
  role: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  id: string | null;
  profilePhotoUrl: string | null;
} {
  try {
    const raw = localStorage.getItem(USER_INFO_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Normalize old role values
      if (parsed.role === "OrganisationSuperAdmin") parsed.role = "SuperAdmin";
      if (parsed.role === "OrganisationAdmin") parsed.role = "Admin";
      return parsed;
    }
  } catch {}
  return {
    role: null,
    firstName: null,
    lastName: null,
    email: null,
    id: null,
    profilePhotoUrl: null,
  };
}

interface State {
  error: { value: boolean; type: any | null; message: string | null };
  profilePhotoUrl: string | null;
  userFirstName: string | null;
  userLastName: string | null;
  userRole: string | null;
  userEmail: string | null;
  userId: string | null;
}

export const useUserStore = defineStore("user", {
  state: (): State => {
    const saved = loadUserInfo();
    return {
      error: { value: false, type: null, message: null },
      profilePhotoUrl: saved.profilePhotoUrl || null,
      userFirstName: saved.firstName,
      userLastName: saved.lastName,
      userRole: saved.role,
      userEmail: saved.email,
      userId: saved.id,
    };
  },

  getters: {
    userInitials: (state) => {
      if (!state.userFirstName && !state.userLastName) return null;
      const firstInitial = state.userFirstName?.charAt(0).toUpperCase() || "";
      const lastInitial = state.userLastName?.charAt(0).toUpperCase() || "";
      return `${firstInitial}${lastInitial}`;
    },
    isSuperAdmin: (state) => {
      const role = state.userRole?.toLowerCase().replace(/\s/g, "") ?? "";
      return role === "superadmin" || role === "organisationsuperadmin";
    },
    isAdmin: (state) => {
      const role = state.userRole?.toLowerCase().replace(/\s/g, "") ?? "";
      return role === "admin" || role === "organisationadmin";
    },
  },

  actions: {
    async show(userId: number): Promise<any> {
      try {
        const response = await userService.show(userId);
        if (response && response.data && response.data.data) {
          const userData = response.data.data;

          // Handle flat API format: { succeeded, data: { id, firstname, lastname, email, imageUrl, ... } }
          if (userData.firstname || userData.email) {
            const data = JSON.stringify({
              customerInfo: {
                firstName: userData.firstname || userData.firstName || "",
                lastName: userData.lastname || userData.lastName || "",
                email: userData.email || "",
                phone: userData.phone || "",
                organisationName: userData.organisationName || "",
                organisationId: userData.organisationId || 0,
                userId: userData.id || userId,
              },
            });
            storeItem(import.meta.env.VITE_USERDETAILS, data);
          } else if (userData.organisation && userData.organisation.user) {
            // Legacy nested format: { data: { organisation: { user: {...} } } }
            const data = JSON.stringify({
              customerInfo: {
                firstName: userData.organisation.user.firstname,
                lastName: userData.organisation.user.lastname,
                email: userData.organisation.user.email,
                phone: userData.organisation.user.email,
                organisationName: userData.organisation.organisationName,
                organisationId: userData.organisation.id,
                userId: userData.organisation.userId,
              },
            });
            storeItem(import.meta.env.VITE_USERDETAILS, data);
          }

          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async fetchAdmin(id: number): Promise<any> {
      try {
        const response = await userService.fetchAdmin(id);
        if (response && response.data && response.data.data) {
          const data = JSON.stringify({
            customerInfo: {
              firstName: response.data.data.organisation.user.firstname,
              lastName: response.data.data.organisation.user.lastname,
              email: response.data.data.organisation.user.email,
              phone: response.data.data.organisation.user.email,
              // wallet: response.data.data.organisation.wallet.balance,
              id: response.data.data.organisation.id,
            },
          });
          storeItem(import.meta.env.VITE_USERDETAILS, data);
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async verify(verify: string): Promise<any> {
      try {
        const response = await userService.verify(verify);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    // async getUsers(): Promise<any> {
    //   try {
    //     const response = await userService.getUsers();
    //     if (response.data) {
    //       return await Promise.resolve(response);
    //     } else if (response.response) {
    //       return await Promise.reject(response.response);
    //     } else {
    //       return await Promise.reject(response.message);
    //     }
    //   } catch (error: any) {
    //     return await Promise.reject(error);
    //   }
    // },

    async getUsers(
      organisationId: number,
      pageSize: number,
      pageNumber: number,
    ): Promise<any> {
      try {
        const response = await userService.getUsers(
          organisationId,
          pageSize,
          pageNumber,
        );
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async getUserRole(userId: number): Promise<any> {
      try {
        const response = await userService.getUserRoleById(userId);
        if (response.succeeded) {
          return response.data;
        } else {
          throw new Error(response.message);
        }
      } catch (error: any) {
        console.error("Error in getUserRole:", error);
        throw error;
      }
    },

    setUserInfo(info: {
      role: string;
      firstName: string;
      lastName: string;
      email: string;
      id: string;
    }) {
      // Normalize role to canonical values
      let role = info.role;
      if (role === "OrganisationSuperAdmin") role = "SuperAdmin";
      if (role === "OrganisationAdmin") role = "Admin";

      this.userRole = role;
      this.userFirstName = info.firstName;
      this.userLastName = info.lastName;
      this.userEmail = info.email;
      this.userId = info.id;
      localStorage.setItem(
        USER_INFO_KEY,
        JSON.stringify({
          role,
          firstName: info.firstName,
          lastName: info.lastName,
          email: info.email,
          id: info.id,
          profilePhotoUrl: this.profilePhotoUrl,
        }),
      );
    },

    clearUserInfo() {
      this.userRole = null;
      this.userFirstName = null;
      this.userLastName = null;
      this.userEmail = null;
      this.userId = null;
      this.profilePhotoUrl = null;
      localStorage.removeItem(USER_INFO_KEY);
    },

    _persistProfilePhoto() {
      try {
        const raw = localStorage.getItem(USER_INFO_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          parsed.profilePhotoUrl = this.profilePhotoUrl;
          localStorage.setItem(USER_INFO_KEY, JSON.stringify(parsed));
        }
      } catch {}
    },

    async fetchAndStoreUserRole(): Promise<void> {
      // Role is already in store (loaded from localStorage on init)
      // Nothing to do unless store is empty
    },

    async fetchRoles(): Promise<any> {
      try {
        const response = await userService.fetchRoles();
        if (response.data) {
          return await Promise.resolve(response.data.data);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async updateRole(userId: number, roleId: number): Promise<any> {
      try {
        const response = await userService.updateRole(userId, roleId);
        if (response.data) {
          return await Promise.resolve(response.data);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async deleteUser(userId: string): Promise<any> {
      try {
        const response = await userService.deleteUser(userId);
        if (response.data) {
          return await Promise.resolve(response.data);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async create(data: Create): Promise<any> {
      try {
        const response = await userService.create(data);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async update(data: Update): Promise<any> {
      try {
        const response = await userService.update(data);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
    async delete(id: string): Promise<any> {
      try {
        const response = await userService.delete(id);
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async adminCount(): Promise<any> {
      try {
        const response = await userService.adminCount();
        if (response.data) {
          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },

    async fetchUserProfilePhoto(userId: number) {
      try {
        // Try both endpoints to get user profile data
        let result = null;

        try {
          // First try the getUserProfile endpoint (returns response.data directly)
          result = await userService.getUserProfile(userId);
        } catch (error) {
          // Fallback to show endpoint (returns full axios response)
          const response = await userService.show(userId);
          result = response?.data;
        }

        // New API format: { succeeded: true, data: { id, firstname, lastname, imageUrl, ... } }
        let imageUrl = null;
        let firstName = null;
        let lastName = null;

        if (result?.succeeded && result.data) {
          const user = result.data;
          imageUrl = user.imageUrl;
          firstName = user.firstname || user.firstName;
          lastName = user.lastname || user.lastName;
        } else if (result?.data?.succeeded && result.data?.data) {
          // Nested structure from show endpoint
          const user = result.data.data;
          imageUrl = user.imageUrl;
          firstName = user.firstname || user.firstName;
          lastName = user.lastname || user.lastName;
        }

        // Update user names
        this.userFirstName = firstName;
        this.userLastName = lastName;

        if (imageUrl) {
          // Add cache-busting timestamp to prevent browser caching issues
          this.profilePhotoUrl = imageUrl.includes("?")
            ? `${imageUrl}&t=${Date.now()}`
            : `${imageUrl}?t=${Date.now()}`;
        } else {
          this.profilePhotoUrl = null;
        }
        this._persistProfilePhoto();
      } catch (error) {
        console.error("💥 Failed to fetch profile photo:", error);
        this.profilePhotoUrl = null;
      }
    },

    updateProfilePhotoUrl(imageUrl: string) {
      this.profilePhotoUrl = imageUrl.includes("?")
        ? `${imageUrl}&t=${Date.now()}`
        : `${imageUrl}?t=${Date.now()}`;
      this._persistProfilePhoto();
    },

    async uploadPassport(file: File, userId: number) {
      try {
        const response = await userService.uploadPassport(file, userId);
        if (response.data) {
          // After successful upload, try to extract imageUrl from response
          const imageUrl =
            response.data?.data?.imageUrl ||
            response.data?.data?.url ||
            response.data?.imageUrl;

          if (imageUrl) {
            this.updateProfilePhotoUrl(imageUrl);
          } else {
            // Re-fetch user profile to get the updated photo URL
            await this.fetchUserProfilePhoto(userId);
          }

          return await Promise.resolve(response);
        } else if (response.response) {
          return await Promise.reject(response.response);
        } else {
          return await Promise.reject(response.message);
        }
      } catch (error: any) {
        return await Promise.reject(error);
      }
    },
  },
});

export default useUserStore;
